import useQueryParams from 'Frontend/hooks/useQueryParams'
import { IForumSearchResults } from 'Frontend/scripts/models/forums.model'
import { getForumSearchResult } from 'Frontend/scripts/services/forums.service'
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

type SearchState = {
  page: number
  query: string
  tags: string[]
}

type ForumQAndAContextType = {
  state: SearchState
  searchHits: IForumSearchResults | null
  setPage: (page: number) => void
  setQuery: (query: string) => void
  setTags: (tags: string[]) => void
}

const ForumQAndAContext = createContext<ForumQAndAContextType | undefined>(undefined)

export const ForumQAndAProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getQueryParam } = useQueryParams()

  const [state, setState] = useState<SearchState>({
    page: getQueryParam('page') !== null ? Number(getQueryParam('page')) : 1,
    query: getQueryParam('query') || '',
    tags: getQueryParam('tags')?.split(',') ?? [],
  })
  const [searchHits, setSearchHits] = useState<IForumSearchResults | null>(null)

  useEffect(() => {
    performSearch(state)
  }, [state])

  useEffect(() => {
    const params = new URLSearchParams()

    if (state.query) params.set('query', state.query)
    if (state.page !== 1) params.set('page', String(state.page))
    if (state.tags.length > 0) params.set('tags', state.tags.join(','))

    const queryString = params.toString()
    const newUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`

    window.history.pushState({}, '', newUrl)
  }, [state])

  const setPage = (page: number) => {
    setState((prev) => ({ ...prev, page }))
  }

  const setQuery = (query: string) => {
    setState((prev) => ({
      ...prev,
      query,
      page: 1, // reset page
    }))
  }

  const setTags = (tags: string[]) => {
    setState((prev) => ({
      ...prev,
      tags,
      page: 1, // reset page
    }))
  }

  const performSearch = useCallback((searchParams: SearchState) => {
    getForumSearchResult(searchParams.query, searchParams.page, searchParams.tags).then((data: IForumSearchResults) => {
      setSearchHits(data)
    })
  }, [])

  return (
    <ForumQAndAContext.Provider value={{ state, searchHits, setPage, setQuery, setTags }}>
      {children}
    </ForumQAndAContext.Provider>
  )
}

export const useSearchContext = (): ForumQAndAContextType => {
  const context = useContext(ForumQAndAContext)
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider')
  }
  return context
}
